import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { hasPermissionToPostListings, showCreateListingLinkForUser } from '../../util/userHelpers';
import { NO_ACCESS_PAGE_POST_LISTINGS, parse } from '../../util/urlHelpers';
import { propTypes } from '../../util/types';
import { isErrorNoPermissionToPostListings } from '../../util/errors';
import { isAtListingCap } from '../../util/listingCaps';
import { canUseListingImport } from '../../util/listingImport/canUseListingImport';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';

import {
  H3,
  Page,
  PaginationLinks,
  UserNav,
  LayoutSingleColumn,
  ListingCapModal,
  NamedLink,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import ManageListingCard from './ManageListingCard/ManageListingCard';

import {
  closeListing,
  openListing,
  getOwnListingsById,
  discardDraft,
  loadListingCapContextThunk,
  saveManageListingsFilters,
} from './ManageListingsPage.duck';
import {
  getManageListingsFiltersFromUser,
  listingVisibleWithManageFilters,
  manageListingsFilterSearchParams,
  resolveManageListingsFilters,
} from './ManageListingsPage.filters';
import css from './ManageListingsPage.module.css';
import DiscardDraftModal from './DiscardDraftModal/DiscardDraftModal';

const Heading = props => {
  const { listingsAreLoaded, pagination, hasActiveFilters, listingCap } = props;
  const hasResults = listingsAreLoaded && pagination.totalItems > 0;
  const hasNoResults = listingsAreLoaded && pagination.totalItems === 0;
  // Filtered-to-empty still shows the count heading so filters remain useful.
  const showCountHeading = hasResults || (hasNoResults && hasActiveFilters);
  const openListingsCap = listingCap ? (
    <p className={css.openListingsCap}>
      <FormattedMessage
        id="ManageListingsPage.openListingsCount"
        values={{ count: listingCap.openCount, max: listingCap.max }}
      />
    </p>
  ) : null;

  return showCountHeading ? (
    <>
      <H3 as="h1" className={listingCap ? css.headingWithOpenListingsCap : css.heading}>
        <FormattedMessage
          id="ManageListingsPage.youHaveListings"
          values={{ count: pagination.totalItems }}
        />
      </H3>
      {openListingsCap}
    </>
  ) : hasNoResults ? (
    <div className={css.noResultsContainer}>
      <H3 as="h1" className={css.headingNoListings}>
        <FormattedMessage id="ManageListingsPage.noResults" />
      </H3>
      {openListingsCap}
      <p className={css.createListingParagraph}>
        <NamedLink className={css.createListingLink} name="NewListingPage">
          <FormattedMessage id="ManageListingsPage.createListing" />
        </NamedLink>
      </p>
    </div>
  ) : null;
};

const PaginationLinksMaybe = props => {
  const {
    listingsAreLoaded,
    pagination,
    page,
    pageSearchParams,
    pageName = 'ManageListingsPage',
    pagePathParams = {},
  } = props;
  return listingsAreLoaded && pagination && pagination.totalPages > 1 ? (
    <PaginationLinks
      className={css.pagination}
      pageName={pageName}
      pagePathParams={pagePathParams}
      pageSearchParams={{ ...pageSearchParams, page }}
      pagination={pagination}
    />
  ) : null;
};

/**
 * The ManageListingsPage component.
 *
 * @component
 * @param {Object} props
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {propTypes.uuid} props.closingListing - The closing listing
 * @param {Object} props.closingListingError - The closing listing error
 * @param {propTypes.error} props.closingListingError.listingId - The closing listing id
 * @param {propTypes.error} props.closingListingError.error - The closing listing error
 * @param {propTypes.ownListing[]} props.listings - The listings
 * @param {function} props.onCloseListing - The onCloseListing function
 * @param {function} props.onDiscardDraft - The onDiscardDraft function
 * @param {function} props.onOpenListing - The onOpenListing function
 * @param {Object} props.openingListing - The opening listing
 * @param {propTypes.uuid} props.openingListing.uuid - The opening listing uuid
 * @param {Object} props.openingListingError - The opening listing error
 * @param {propTypes.uuid} props.openingListingError.listingId - The opening listing id
 * @param {propTypes.error} props.openingListingError.error - The opening listing error
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {boolean} props.queryInProgress - Whether the query is in progress
 * @param {propTypes.error} props.queryListingsError - The query listings error
 * @param {Object} props.queryParams - The query params
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {function} props.onManageDisableScrolling - The onManageDisableScrolling function
 * @returns {JSX.Element} manage listings page component
 */
export const ManageListingsPageComponent = props => {
  const [listingMenuOpen, setListingMenuOpen] = useState(null);
  const [discardDraftModalOpen, setDiscardDraftModalOpen] = useState(null);
  const [discardDraftModalId, setDiscardDraftModalId] = useState(null);
  const [listingCapModalOpen, setListingCapModalOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const intl = useIntl();

  const {
    currentUser,
    closingListing,
    closingListingError,
    discardingDraft,
    discardingDraftError,
    listings = [],
    listingCap,
    onCloseListing,
    onDiscardDraft,
    onLoadListingCap,
    onOpenListing,
    onSaveManageListingsFilters,
    openingListing,
    openingListingError,
    pagination,
    queryInProgress,
    queryListingsError,
    queryParams,
    scrollingDisabled,
    onManageDisableScrolling,
    hasFinishTableDrafts = false,
    embedded = false,
    profileId = null,
    onListingMutated,
  } = props;

  const parsedSearch = parse(location.search);
  const resolvedFilters = resolveManageListingsFilters({
    queryParams: parsedSearch,
    currentUser,
  });
  const savedFilters = getManageListingsFiltersFromUser(currentUser);
  // Embedded profile tab keeps the toggle in memory so it can filter the
  // loaded listings without a URL change and a refetch flash.
  const [embeddedFilters, setEmbeddedFilters] = useState(null);
  const hideClosed = embedded
    ? (embeddedFilters?.hideClosed ?? savedFilters.hideClosed)
    : resolvedFilters.hideClosed;
  const hideDrafts = embedded
    ? (embeddedFilters?.hideDrafts ?? savedFilters.hideDrafts)
    : resolvedFilters.hideDrafts;

  const setFilters = next => {
    if (embedded) {
      const nextFilters = {
        hideClosed: !!next.hideClosed,
        hideDrafts: !!next.hideDrafts,
      };
      setEmbeddedFilters(nextFilters);
      onSaveManageListingsFilters(nextFilters).catch(() => {
        // Logged in saveManageListingsFiltersThunk; keep the in-memory toggle.
      });
      return;
    }
    const searchParams = manageListingsFilterSearchParams({
      hideClosed: next.hideClosed,
      hideDrafts: next.hideDrafts,
      page: 1,
    });
    history.push(
      createResourceLocatorString('ManageListingsPage', routeConfiguration, {}, searchParams)
    );
    onSaveManageListingsFilters({
      hideClosed: !!next.hideClosed,
      hideDrafts: !!next.hideDrafts,
    }).catch(() => {
      // Logged in saveManageListingsFiltersThunk; keep optimistic UI/URL.
    });
  };

  // Sync URL to effective filters (profile defaults) without writing profile.
  // Wait until the listings query settles so replace→loadData can skip a matching refetch.
  useEffect(() => {
    if (embedded || queryInProgress) {
      return;
    }
    const search = parse(location.search);
    const urlHideClosedOn = search.hideClosed === true;
    const urlHideDraftsOn = search.hideDrafts === true;
    if (urlHideClosedOn === hideClosed && urlHideDraftsOn === hideDrafts) {
      return;
    }

    const { hideClosed: _hideClosed, hideDrafts: _hideDrafts, ...rest } = search;
    const searchParams = {
      ...rest,
      ...manageListingsFilterSearchParams({
        hideClosed,
        hideDrafts,
        page: search.page,
      }),
    };
    history.replace(
      createResourceLocatorString('ManageListingsPage', routeConfiguration, {}, searchParams)
    );
  }, [
    embedded,
    hideClosed,
    hideDrafts,
    history,
    location.search,
    queryInProgress,
    routeConfiguration,
  ]);

  useEffect(() => {
    if (isErrorNoPermissionToPostListings(openingListingError?.error)) {
      const noAccessPagePath = pathByRouteName('NoAccessPage', routeConfiguration, {
        missingAccessRight: NO_ACCESS_PAGE_POST_LISTINGS,
      });
      history.push(noAccessPagePath);
      return;
    }
    const capErrorCode = openingListingError?.error?.code;
    if (
      capErrorCode === 'listing-cap-reached' ||
      capErrorCode === 'listing-cap-unknown' ||
      capErrorCode === 'listing-cap-check-failed'
    ) {
      setListingCapModalOpen(true);
    }
  }, [openingListingError, history, routeConfiguration]);

  const onToggleMenu = listing => {
    setListingMenuOpen(listing);
  };

  const handleOpenListing = async listingId => {
    const hasPostingRights = hasPermissionToPostListings(currentUser);

    if (!hasPostingRights) {
      const noAccessPagePath = pathByRouteName('NoAccessPage', routeConfiguration, {
        missingAccessRight: NO_ACCESS_PAGE_POST_LISTINGS,
      });
      history.push(noAccessPagePath);
      return;
    }

    // Avoid overlapping reopens racing the open-count refresh.
    if (openingListing) {
      return;
    }

    let cap = listingCap;
    if (!cap && onLoadListingCap) {
      try {
        cap = await onLoadListingCap(config);
      } catch (e) {
        setListingCapModalOpen(true);
        return;
      }
    }

    if (!cap || cap.isAtCap || isAtListingCap(cap.openCount, cap.max)) {
      setListingCapModalOpen(true);
      return;
    }

    const pending = onOpenListing(listingId);
    if (onListingMutated) {
      Promise.resolve(pending).then(() => onListingMutated());
    }
  };

  const openDiscardDraftModal = listingId => {
    setDiscardDraftModalId(listingId);
    setDiscardDraftModalOpen(true);
  };

  const handleDiscardDraft = () => {
    onDiscardDraft(discardDraftModalId);
    setDiscardDraftModalOpen(false);
    setDiscardDraftModalId(null);
  };

  const hasPaginationInfo = !!pagination && pagination.totalItems != null;
  const listingsAreLoaded = !queryInProgress && hasPaginationInfo;
  const hasActiveFilters = hideClosed || hideDrafts;
  // Keep toggles visible while refetching if filters are already on (URL-driven).
  const showFilters = hasActiveFilters || (listingsAreLoaded && (pagination?.totalItems || 0) > 0);
  const filterSearchParams = manageListingsFilterSearchParams({ hideClosed, hideDrafts });
  const shownIdsRef = useRef(null);
  const filterKeyRef = useRef(null);
  const [cardMotion, setCardMotion] = useState({
    entering: new Set(),
    exiting: new Set(),
  });

  const listingsMotionKey = listings
    .map(listing => `${listing.id.uuid}:${listing.attributes?.state}`)
    .join('|');

  useEffect(() => {
    if (!embedded) {
      return undefined;
    }
    const visible = new Set(
      listings
        .filter(listing => listingVisibleWithManageFilters(listing, { hideClosed, hideDrafts }))
        .map(listing => listing.id.uuid)
    );
    const filterKey = `${hideClosed}:${hideDrafts}`;
    const filtersChanged = filterKeyRef.current != null && filterKeyRef.current !== filterKey;
    filterKeyRef.current = filterKey;
    if (!filtersChanged) {
      shownIdsRef.current = visible;
      return undefined;
    }
    const entering = new Set();
    const exiting = new Set();
    const previouslyShown = shownIdsRef.current || new Set();
    visible.forEach(id => {
      if (!previouslyShown.has(id)) {
        entering.add(id);
      }
    });
    previouslyShown.forEach(id => {
      if (!visible.has(id)) {
        exiting.add(id);
      }
    });
    shownIdsRef.current = visible;
    if (entering.size === 0 && exiting.size === 0) {
      return undefined;
    }
    setCardMotion({ entering, exiting });
    const timer = setTimeout(
      () => setCardMotion({ entering: new Set(), exiting: new Set() }),
      220
    );
    return () => clearTimeout(timer);
  }, [embedded, hideClosed, hideDrafts, listingsMotionKey]);

  const renderedListings = embedded
    ? listings.filter(listing => {
        const id = listing.id.uuid;
        return (
          listingVisibleWithManageFilters(listing, { hideClosed, hideDrafts }) ||
          cardMotion.exiting.has(id)
        );
      })
    : listings;
  const isFilteredEmpty = embedded
    ? hasActiveFilters &&
      listingsAreLoaded &&
      renderedListings.length === 0 &&
      cardMotion.exiting.size === 0
    : hasActiveFilters && listingsAreLoaded && pagination?.totalItems === 0;

  const loadingResults = (
    <div className={css.messagePanel}>
      <H3 as="h2" className={css.heading}>
        <FormattedMessage id="ManageListingsPage.loadingOwnListings" />
      </H3>
    </div>
  );

  const queryError = (
    <div className={css.messagePanel}>
      <H3 as="h2" className={css.heading}>
        <FormattedMessage id="ManageListingsPage.queryError" />
      </H3>
    </div>
  );

  const closingErrorListingId = !!closingListingError && closingListingError.listingId;
  const openingErrorListingId = !!openingListingError && openingListingError.listingId;
  const discardingErrorListingId = !!discardingDraftError && discardingDraft.listingId;

  const panelWidth = 62.5;
  // Render hints for responsive image
  const renderSizes = [
    `(max-width: 767px) 100vw`,
    `(max-width: 1920px) ${panelWidth / 2}vw`,
    `${panelWidth / 3}vw`,
  ].join(', ');

  const showManageListingsLink = showCreateListingLinkForUser(config, currentUser);

  const body = (
    <>
      {queryInProgress ? loadingResults : null}
      {queryListingsError ? queryError : null}

      <div className={css.listingPanel}>
        <Heading
          listingsAreLoaded={listingsAreLoaded}
          pagination={
            embedded &&
            pagination &&
            (pagination.totalPages || 1) <= 1 &&
            listings.length > 0
              ? {
                  ...pagination,
                  totalItems: listings.filter(listing =>
                    listingVisibleWithManageFilters(listing, { hideClosed, hideDrafts })
                  ).length,
                }
              : pagination
          }
          hasActiveFilters={hasActiveFilters}
          listingCap={listingCap}
        />

        {canUseListingImport(currentUser) ? (
          <div className={css.importActions}>
            <NamedLink className={css.importActionLink} name="ImportListingsPage">
              <FormattedMessage id="ManageListingsPage.importListings" />
            </NamedLink>
            {hasFinishTableDrafts ? (
              <NamedLink className={css.importActionLink} name="FinishImportedListingsPage">
                <FormattedMessage id="ManageListingsPage.finishImportedListings" />
              </NamedLink>
            ) : null}
          </div>
        ) : null}

        {showFilters ? (
          <div
            className={css.filters}
            role="group"
            aria-label={intl.formatMessage({ id: 'ManageListingsPage.filtersLabel' })}
          >
            <label className={css.filterToggle}>
              <input
                className={css.filterCheckbox}
                type="checkbox"
                checked={hideClosed}
                onChange={e => setFilters({ hideClosed: e.target.checked, hideDrafts })}
              />
              <span className={css.toggleTrack} aria-hidden="true">
                <span className={css.toggleThumb} />
              </span>
              <span className={css.filterText}>
                <FormattedMessage id="ManageListingsPage.hideClosed" />
              </span>
            </label>
            <label className={css.filterToggle}>
              <input
                className={css.filterCheckbox}
                type="checkbox"
                checked={hideDrafts}
                onChange={e => setFilters({ hideClosed, hideDrafts: e.target.checked })}
              />
              <span className={css.toggleTrack} aria-hidden="true">
                <span className={css.toggleThumb} />
              </span>
              <span className={css.filterText}>
                <FormattedMessage id="ManageListingsPage.hideDrafts" />
              </span>
            </label>
          </div>
        ) : null}

        {renderedListings.length > 0 ? (
          <ul className={css.listingCards}>
            {renderedListings.map(l => (
              <li
                key={l.id.uuid}
                className={[
                  css.listingCard,
                  cardMotion.entering.has(l.id.uuid) ? css.listingCardEnter : '',
                  cardMotion.exiting.has(l.id.uuid) ? css.listingCardExit : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <ManageListingCard
                  listing={l}
                  isMenuOpen={!!listingMenuOpen && listingMenuOpen.id.uuid === l.id.uuid}
                  actionsInProgressListingId={openingListing || closingListing || discardingDraft}
                  onToggleMenu={onToggleMenu}
                  onCloseListing={listingId => {
                    const pending = onCloseListing(listingId);
                    if (onListingMutated) {
                      Promise.resolve(pending).then(() => onListingMutated());
                    }
                    return pending;
                  }}
                  onOpenListing={handleOpenListing}
                  onDiscardDraft={openDiscardDraftModal}
                  hasOpeningError={openingErrorListingId.uuid === l.id.uuid}
                  hasClosingError={closingErrorListingId.uuid === l.id.uuid}
                  hasDiscardingError={discardingErrorListingId.uuid === l.id.uuid}
                  renderSizes={renderSizes}
                />
              </li>
            ))}
          </ul>
        ) : null}

        {isFilteredEmpty ? (
          <p className={css.filteredEmpty}>
            <FormattedMessage id="ManageListingsPage.filteredEmpty" />
          </p>
        ) : null}

        {onManageDisableScrolling && discardDraftModalOpen ? (
          <DiscardDraftModal
            id="ManageListingsPage"
            isOpen={discardDraftModalOpen}
            onManageDisableScrolling={onManageDisableScrolling}
            onCloseModal={() => setDiscardDraftModalOpen(false)}
            onDiscardDraft={handleDiscardDraft}
            focusElementId={
              discardDraftModalId ? `discardButton_${discardDraftModalId.uuid}` : null
            }
          />
        ) : null}

        {onManageDisableScrolling && listingCapModalOpen ? (
          <ListingCapModal
            id="ManageListingsPage.listingCapModal"
            isOpen={listingCapModalOpen}
            onClose={() => setListingCapModalOpen(false)}
            onManageDisableScrolling={onManageDisableScrolling}
            userTypeLabel={listingCap?.userTypeLabel || ''}
            baseMax={listingCap?.baseMax ?? 50}
            max={listingCap?.max ?? 50}
            isGroupBump={!!listingCap?.isGroupBump}
          />
        ) : null}

        <PaginationLinksMaybe
          listingsAreLoaded={listingsAreLoaded}
          pagination={pagination}
          page={queryParams ? queryParams.page : 1}
          pageSearchParams={embedded ? { tab: 'listings' } : filterSearchParams}
          pageName={embedded ? 'ProfilePage' : 'ManageListingsPage'}
          pagePathParams={embedded ? { id: profileId } : {}}
        />
      </div>
    </>
  );

  if (embedded) {
    return body;
  }

  return (
    <Page
      title={intl.formatMessage({ id: 'ManageListingsPage.title' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        topbar={
          <>
            <TopbarContainer />
            <UserNav
              currentPage="ManageListingsPage"
              showManageListingsLink={showManageListingsLink}
            />
          </>
        }
      >
        {body}
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    queryInProgress,
    queryListingsError,
    queryParams,
    openingListing,
    openingListingError,
    closingListing,
    closingListingError,
    discardingDraft,
    discardingDraftError,
    listingCap,
    hasFinishTableDrafts,
  } = state.ManageListingsPage;
  const listings = getOwnListingsById(state, currentPageResultIds);
  return {
    currentUser,
    currentPageResultIds,
    listings,
    pagination,
    queryInProgress,
    queryListingsError,
    queryParams,
    scrollingDisabled: isScrollingDisabled(state),
    openingListing,
    openingListingError,
    closingListing,
    closingListingError,
    discardingDraft,
    discardingDraftError,
    listingCap,
    hasFinishTableDrafts,
  };
};

const mapDispatchToProps = dispatch => ({
  onCloseListing: listingId => dispatch(closeListing(listingId)),
  onOpenListing: listingId => dispatch(openListing(listingId)),
  onLoadListingCap: config => dispatch(loadListingCapContextThunk({ config })).unwrap(),
  onDiscardDraft: listingId => dispatch(discardDraft(listingId)),
  onSaveManageListingsFilters: filters => dispatch(saveManageListingsFilters(filters)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const ManageListingsPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ManageListingsPageComponent);

export default ManageListingsPage;
